import type { PageContext } from "../chat/chat-view";
import { type Remark, type RemarkOptions, remarksFor } from "./page-remarks";

/**
 * When the cogent speaks up, and what it says: `onChange` gets the remark
 * to show, or null to take it down.
 */
export class RemarkScheduler {
  private page: PageContext | null = null;
  private current: Remark | null = null;
  private readonly said = new Set<string>();
  private spoke = false;
  private paused = false;
  private holding = false;
  private lastShownAt = -Infinity;
  private quietUntil = -Infinity;
  private pending: unknown = null;
  private hiding: unknown = null;
  private readonly o: Required<RemarkOptions>;
  private readonly onChange: (remark: Remark | null) => void;
  constructor({
    onChange,
    options = {},
  }: {
    onChange: (remark: Remark | null) => void;
    options?: RemarkOptions;
  }) {
    this.onChange = onChange;
    this.o = {
      delayMs: 1800,
      cooldownMs: 30000,
      dismissedMs: 120000,
      charsPerSecond: 14,
      lingerMs: 6000,
      now: () => Date.now(),
      setTimeout: (fn, ms) => setTimeout(fn, ms),
      clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
      ...options,
    };
  }
  /** A new page visit (a new path): it may speak once on it. */
  visit(page: PageContext | null) {
    this.page = page;
    this.spoke = false;
    this.hide();
    this.schedule();
  }
  /** The same page, its facts filled in as its data loads. */
  update(page: PageContext | null) {
    this.page = page;

    if (!this.current) this.schedule();
  }
  /** Paused while its chat or its page is open: anything up comes down. */
  pause({ paused }: { paused: boolean }) {
    if (paused === this.paused) return;

    this.paused = paused;

    if (paused) {
      this.cancel();
      this.hide();
    } else this.schedule();
  }
  /** Kept up while the pointer is on it; let go, it lingers a moment. */
  hold({ on }: { on: boolean }) {
    this.holding = on;

    if (!this.current) return;

    this.stopHiding();

    if (!on) this.hiding = this.o.setTimeout(() => this.hide(), 2500);
  }
  /** Dismissed: down, and quiet for a while. */
  dismiss() {
    this.quietUntil = this.o.now() + this.o.dismissedMs;
    this.hide();
  }
  dispose() {
    this.cancel();
    this.stopHiding();
  }
  private schedule() {
    this.cancel();

    if (this.paused || this.spoke || this.current) return;

    if (!remarksFor(this.page).some((r) => !this.said.has(r.topic))) return;

    const now = this.o.now();
    const wait = Math.max(
      this.o.delayMs,
      this.lastShownAt + this.o.cooldownMs - now,
      this.quietUntil - now,
    );
    this.pending = this.o.setTimeout(() => this.speak(), wait);
  }
  private speak() {
    this.pending = null;

    if (this.paused || this.spoke || this.current) return;

    const remark = remarksFor(this.page).find((r) => !this.said.has(r.topic));

    if (!remark) return;

    this.said.add(remark.topic);
    this.spoke = true;
    this.lastShownAt = this.o.now();
    this.current = remark;
    this.onChange(remark);

    if (!this.holding)
      this.hiding = this.o.setTimeout(
        () => this.hide(),
        (remark.text.length / this.o.charsPerSecond) * 1000 + this.o.lingerMs,
      );
  }
  private hide() {
    this.stopHiding();

    if (!this.current) return;

    this.current = null;
    this.holding = false;
    this.onChange(null);
  }
  private cancel() {
    if (this.pending !== null) this.o.clearTimeout(this.pending);

    this.pending = null;
  }
  private stopHiding() {
    if (this.hiding !== null) this.o.clearTimeout(this.hiding);

    this.hiding = null;
  }
}
